import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Clock, Flag, ChevronLeft, ChevronRight, Check, X, AlertTriangle, Menu, Grid3X3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Hebrew subject names
const SUBJECT_HEBREW = {
  'civil-procedure': 'סדר דין אזרחי',
  'contracts': 'דיני חוזים',
  'torts': 'דיני נזיקין',
  'property': 'דיני קניין',
  'evidence_law': 'דיני ראיות',
  'legal_ethics': 'אתיקה מקצועית',
  'administrative_law': 'משפט מנהלי',
  'criminal_law': 'דיני עונשין',
  'labor_law': 'דיני עבודה',
  'corporations_law': 'דיני חברות'
};

// ==================== FULL SIMULATOR COMPONENT ====================
export const FullSimulator = ({ userId, onComplete, onBack }) => {
  // Session state
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Answer tracking
  const [answers, setAnswers] = useState({}); // { questionId: selectedAnswer }
  const [markedForReview, setMarkedForReview] = useState(new Set());
  
  // UI state
  const [showNavigator, setShowNavigator] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Timer
  const [timeLeft, setTimeLeft] = useState(180 * 60); // 3 hours in seconds
  const timerRef = useRef(null);

  // Initialize session
  useEffect(() => {
    const startSession = async () => {
      try {
        setLoading(true);
        const response = await axios.post(`${API}/adaptive/session/start`, {
          user_id: userId,
          mode: 'simulator'
        });
        
        setSession(response.data);
        
        if (response.data.time_limit) {
          setTimeLeft(response.data.time_limit);
        }
        
        // Fetch all questions for the session
        const sessionData = await axios.get(`${API}/adaptive/session/${response.data.session_id}`);
        const loadedQuestions = sessionData.data.questions || [];
        setQuestions(loadedQuestions);
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to start simulator session:', err);
        setError('שגיאה בטעינת הסימולטור');
        setLoading(false);
      }
    };
    
    startSession();
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [userId]);

  // Timer countdown
  useEffect(() => {
    if (loading || !session) return;
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmitExam(true); // Auto submit when time runs out
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [loading, session]);

  // Format time display
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get time urgency color
  const getTimeColor = () => {
    if (timeLeft < 300) return 'text-red-500 animate-pulse'; // < 5 min
    if (timeLeft < 900) return 'text-orange-400'; // < 15 min
    if (timeLeft < 1800) return 'text-yellow-400'; // < 30 min
    return 'text-white';
  };

  // Current question
  const currentQuestion = questions[currentIndex];

  // Handle answer selection
  const handleSelectAnswer = (answerIndex) => {
    if (!currentQuestion) return;
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: answerIndex
    }));
  };

  // Toggle mark for review
  const toggleMarkForReview = () => {
    if (!currentQuestion) return;
    setMarkedForReview(prev => {
      const newSet = new Set(prev);
      if (newSet.has(currentQuestion.id)) {
        newSet.delete(currentQuestion.id);
      } else {
        newSet.add(currentQuestion.id);
      }
      return newSet;
    });
  };

  // Navigation
  const goToQuestion = (index) => {
    if (index >= 0 && index < questions.length) {
      setCurrentIndex(index);
      setShowNavigator(false);
    }
  };

  const goNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const goPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  // Get question status
  const getQuestionStatus = (question, index) => {
    const isAnswered = answers.hasOwnProperty(question.id);
    const isMarked = markedForReview.has(question.id);
    const isCurrent = index === currentIndex;
    
    return { isAnswered, isMarked, isCurrent };
  };

  // Calculate statistics
  const getStats = () => {
    const total = questions.length;
    const answered = Object.keys(answers).length;
    const marked = markedForReview.size;
    const unanswered = total - answered;
    
    return { total, answered, marked, unanswered };
  };

  // Submit exam
  const handleSubmitExam = async (isAutoSubmit = false) => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    setShowConfirmSubmit(false);
    
    try {
      // Submit all answers
      for (const [questionId, selectedAnswer] of Object.entries(answers)) {
        await axios.post(`${API}/adaptive/answer/submit`, {
          session_id: session.session_id,
          question_id: questionId,
          selected_answer: selectedAnswer,
          time_taken: 60 // Average time per question
        });
      }
      
      // Complete session
      const result = await axios.post(`${API}/adaptive/session/${session.session_id}/complete`);
      
      onComplete({
        ...result.data,
        timeSpent: (180 * 60) - timeLeft,
        autoSubmitted: isAutoSubmit
      });
    } catch (err) {
      console.error('Failed to submit exam:', err);
      setError('שגיאה בשליחת המבחן');
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin text-6xl mb-4">📝</div>
          <p className="text-white text-xl font-medium">מכין את הסימולטור...</p>
          <p className="text-slate-400 mt-2">טוען 100 שאלות</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <p className="text-red-400 text-xl font-medium">{error}</p>
          <button
            onClick={onBack}
            className="mt-6 bg-slate-700 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-600"
          >
            חזרה
          </button>
        </div>
      </div>
    );
  }

  const stats = getStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" dir="rtl">
      {/* Top Bar */}
      <header className="bg-slate-800/90 backdrop-blur-lg border-b border-slate-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Back button */}
            <button
              onClick={() => setShowConfirmSubmit(true)}
              className="text-slate-400 hover:text-white transition-colors flex items-center gap-2"
              data-testid="back-button"
            >
              <ChevronRight className="w-5 h-5" />
              <span className="hidden sm:inline">יציאה</span>
            </button>
            
            {/* Center: Timer */}
            <div className={`flex items-center gap-2 font-mono text-xl sm:text-2xl font-bold ${getTimeColor()}`}>
              <Clock className="w-5 h-5 sm:w-6 sm:h-6" />
              <span data-testid="timer">{formatTime(timeLeft)}</span>
            </div>
            
            {/* Right: Navigator toggle + Submit */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowNavigator(!showNavigator)}
                className={`p-2 rounded-lg transition-colors ${showNavigator ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-300 hover:text-white'}`}
                data-testid="toggle-navigator"
                aria-label="פתח ניווט שאלות"
              >
                <Grid3X3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowConfirmSubmit(true)}
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:shadow-lg transition-all"
                data-testid="submit-exam-button"
              >
                סיום מבחן
              </button>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-3 flex items-center gap-4 text-sm">
            <div className="flex-1 bg-slate-700 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-300"
                style={{ width: `${(stats.answered / stats.total) * 100}%` }}
              />
            </div>
            <span className="text-slate-400 whitespace-nowrap">
              {stats.answered} / {stats.total}
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex">
        {/* Question Navigator Sidebar */}
        {showNavigator && (
          <aside 
            className="fixed inset-y-0 right-0 w-80 bg-slate-800/95 backdrop-blur-lg border-l border-slate-700 z-30 pt-24 pb-6 px-4 overflow-y-auto"
            role="navigation"
            aria-label="ניווט שאלות"
          >
            <div className="mb-6">
              <h3 className="text-white font-bold text-lg mb-4">ניווט שאלות</h3>
              
              {/* Legend */}
              <div className="flex flex-wrap gap-3 text-xs mb-4">
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded bg-green-500"></div>
                  <span className="text-slate-400">נענתה</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded bg-yellow-500"></div>
                  <span className="text-slate-400">מסומנת</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded bg-slate-600"></div>
                  <span className="text-slate-400">לא נענתה</span>
                </div>
              </div>
              
              {/* Stats summary */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-slate-700/50 p-2 rounded-lg text-center">
                  <div className="text-green-400 font-bold">{stats.answered}</div>
                  <div className="text-xs text-slate-400">נענו</div>
                </div>
                <div className="bg-slate-700/50 p-2 rounded-lg text-center">
                  <div className="text-yellow-400 font-bold">{stats.marked}</div>
                  <div className="text-xs text-slate-400">מסומנות</div>
                </div>
                <div className="bg-slate-700/50 p-2 rounded-lg text-center">
                  <div className="text-slate-400 font-bold">{stats.unanswered}</div>
                  <div className="text-xs text-slate-400">נותרו</div>
                </div>
              </div>
            </div>
            
            {/* Question grid */}
            <div className="grid grid-cols-5 gap-2">
              {questions.map((question, index) => {
                const { isAnswered, isMarked, isCurrent } = getQuestionStatus(question, index);
                
                let bgColor = 'bg-slate-600 hover:bg-slate-500';
                if (isCurrent) {
                  bgColor = 'bg-purple-500 ring-2 ring-purple-300';
                } else if (isMarked && isAnswered) {
                  bgColor = 'bg-gradient-to-br from-green-500 to-yellow-500';
                } else if (isMarked) {
                  bgColor = 'bg-yellow-500 hover:bg-yellow-400';
                } else if (isAnswered) {
                  bgColor = 'bg-green-500 hover:bg-green-400';
                }
                
                return (
                  <button
                    key={question.id}
                    onClick={() => goToQuestion(index)}
                    className={`w-full aspect-square rounded-lg font-bold text-sm text-white transition-all ${bgColor}`}
                    data-testid={`nav-question-${index + 1}`}
                    aria-label={`שאלה ${index + 1}${isAnswered ? ', נענתה' : ''}${isMarked ? ', מסומנת לעיון' : ''}`}
                    aria-current={isCurrent ? 'true' : undefined}
                  >
                    {index + 1}
                    {isMarked && (
                      <Flag className="w-2 h-2 absolute top-0 right-0 text-white" />
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Quick jump to unanswered */}
            {stats.unanswered > 0 && (
              <button
                onClick={() => {
                  const firstUnanswered = questions.findIndex(q => !answers.hasOwnProperty(q.id));
                  if (firstUnanswered >= 0) goToQuestion(firstUnanswered);
                }}
                className="w-full mt-4 bg-slate-700 text-slate-300 py-3 rounded-xl font-medium hover:bg-slate-600 transition-colors"
              >
                עבור לשאלה הבאה שלא נענתה
              </button>
            )}
            
            {/* Quick jump to marked */}
            {stats.marked > 0 && (
              <button
                onClick={() => {
                  const firstMarked = questions.findIndex(q => markedForReview.has(q.id));
                  if (firstMarked >= 0) goToQuestion(firstMarked);
                }}
                className="w-full mt-2 bg-yellow-500/20 text-yellow-400 py-3 rounded-xl font-medium hover:bg-yellow-500/30 transition-colors"
              >
                עבור לשאלה מסומנת
              </button>
            )}
          </aside>
        )}

        {/* Question Area */}
        <main className={`flex-1 p-4 sm:p-6 transition-all ${showNavigator ? 'mr-80' : ''}`}>
          <div className="max-w-4xl mx-auto">
            {currentQuestion && (
              <article className="bg-slate-800/50 backdrop-blur-lg rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-700/50">
                {/* Question Header */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                  <div className="flex items-center gap-3">
                    <span className="bg-slate-700 text-white px-4 py-2 rounded-xl font-bold text-lg">
                      {currentIndex + 1}
                    </span>
                    <span className="text-slate-500">מתוך {questions.length}</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {/* Subject badge */}
                    <span className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full text-sm font-medium">
                      {SUBJECT_HEBREW[currentQuestion.subject] || currentQuestion.subject}
                    </span>
                    
                    {/* Mark for review button */}
                    <button
                      onClick={toggleMarkForReview}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                        markedForReview.has(currentQuestion.id)
                          ? 'bg-yellow-500 text-slate-900'
                          : 'bg-slate-700 text-slate-300 hover:text-white'
                      }`}
                      data-testid="mark-for-review"
                      aria-pressed={markedForReview.has(currentQuestion.id)}
                    >
                      <Flag className="w-4 h-4" />
                      <span className="hidden sm:inline">
                        {markedForReview.has(currentQuestion.id) ? 'מסומנת' : 'סמן לעיון'}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Question Text */}
                <h2 className="text-lg sm:text-xl text-white font-medium leading-relaxed mb-8">
                  {currentQuestion.text}
                </h2>

                {/* Answer Options */}
                <div className="space-y-3" role="radiogroup" aria-label="אפשרויות תשובה">
                  {currentQuestion.options.map((option, index) => {
                    const isSelected = answers[currentQuestion.id] === index;
                    
                    return (
                      <button
                        key={index}
                        onClick={() => handleSelectAnswer(index)}
                        className={`w-full p-4 rounded-xl border-2 text-right text-white transition-all ${
                          isSelected
                            ? 'bg-purple-500/20 border-purple-500'
                            : 'bg-slate-700/50 border-slate-600 hover:border-purple-400 hover:bg-slate-700/70'
                        }`}
                        data-testid={`option-${index}`}
                        role="radio"
                        aria-checked={isSelected}
                      >
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ml-3 transition-colors ${
                          isSelected ? 'bg-purple-500 text-white' : 'bg-slate-600 text-slate-300'
                        }`}>
                          {['א', 'ב', 'ג', 'ד'][index]}
                        </span>
                        <span>{option}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-700">
                  <button
                    onClick={goPrev}
                    disabled={currentIndex === 0}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                      currentIndex === 0
                        ? 'bg-slate-700/30 text-slate-500 cursor-not-allowed'
                        : 'bg-slate-700 text-white hover:bg-slate-600'
                    }`}
                    data-testid="prev-question"
                  >
                    <ChevronRight className="w-5 h-5" />
                    הקודמת
                  </button>
                  
                  <button
                    onClick={() => setShowNavigator(!showNavigator)}
                    className="bg-slate-700 text-white px-4 py-3 rounded-xl font-medium hover:bg-slate-600 sm:hidden"
                  >
                    <Grid3X3 className="w-5 h-5" />
                  </button>
                  
                  {currentIndex === questions.length - 1 ? (
                    <button
                      onClick={() => setShowConfirmSubmit(true)}
                      className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-lg transition-all"
                      data-testid="finish-exam"
                    >
                      סיום מבחן
                      <Check className="w-5 h-5" />
                    </button>
                  ) : (
                    <button
                      onClick={goNext}
                      className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:shadow-lg transition-all"
                      data-testid="next-question"
                    >
                      הבאה
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </article>
            )}
          </div>
        </main>
      </div>

      {/* Confirm Submit Modal */}
      {showConfirmSubmit && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
        >
          <div className="bg-slate-800 rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-slate-700">
            <h3 id="confirm-title" className="text-2xl font-black text-white mb-4">
              סיום המבחן?
            </h3>
            
            {/* Summary */}
            <div className="bg-slate-700/50 rounded-2xl p-4 mb-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-400">{stats.answered}</div>
                  <div className="text-xs text-slate-400">נענו</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-400">{stats.marked}</div>
                  <div className="text-xs text-slate-400">מסומנות</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-400">{stats.unanswered}</div>
                  <div className="text-xs text-slate-400">לא נענו</div>
                </div>
              </div>
            </div>
            
            {/* Warnings */}
            {stats.unanswered > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-400 font-medium">יש לך {stats.unanswered} שאלות שלא נענו!</p>
                  <p className="text-slate-400 text-sm mt-1">שאלות ללא תשובה ייחשבו כשגויות</p>
                </div>
              </div>
            )}
            
            {stats.marked > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
                <Flag className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-yellow-400">יש לך {stats.marked} שאלות מסומנות לעיון</p>
              </div>
            )}
            
            {/* Time remaining */}
            <div className="text-center mb-6">
              <span className="text-slate-400">זמן שנותר: </span>
              <span className={`font-mono font-bold ${getTimeColor()}`}>{formatTime(timeLeft)}</span>
            </div>
            
            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={() => setShowConfirmSubmit(false)}
                className="flex-1 bg-slate-700 text-white py-4 rounded-xl font-bold hover:bg-slate-600 transition-colors"
                data-testid="cancel-submit"
              >
                חזרה למבחן
              </button>
              <button
                onClick={() => handleSubmitExam(false)}
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-xl font-bold hover:shadow-lg transition-all disabled:opacity-50"
                data-testid="confirm-submit"
              >
                {isSubmitting ? 'שולח...' : 'שלח מבחן'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FullSimulator;
